import { ImageEditAltTextModal } from '@/components/image-library/image-edit-alt-text-modal';
import { ImageEditNameModal } from '@/components/image-library/image-edit-name-modal';
import { ImageLibraryBrowseShell } from '@/components/image-library/image-library-browse-shell';
import { ImagePreviewModal } from '@/components/image-library/image-preview-modal';
import { UploadImageModal } from '@/components/image-library/image-upload-modal';
import { useImageLibraryListing } from '@/components/image-library/use-image-library-listing';
import type { MaImage, UploadedImageResponse } from '@/lib/zybooks-api';
import { useState } from 'react';
import { toast } from 'sonner';

export default function ImageLibraryPage() {
    const [imagePreview, setImagePreview] = useState<MaImage | null>(null);
    const [imageToEdit, setImageToEdit] = useState<MaImage | null>(null);
    const [imageToEditAltText, setImageToEditAltText] = useState<MaImage | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);

    const listing = useImageLibraryListing();

    const { allImages, setAllImages, setTotalCount, setOnlyUnused, setOnlyMyUploads } = listing;

    const handleImageRename = async (oldImage: MaImage, newImage: MaImage) => {
        const updatedImages = allImages.map(img => (img.ma_image_id === oldImage.ma_image_id ? newImage : img));
        setAllImages(updatedImages);
    };

    const handleImageAltTextUpdate = (updatedImage: MaImage) => {
        setAllImages(prev => prev.map(img => (img.ma_image_id === updatedImage.ma_image_id ? updatedImage : img)));
        setImagePreview(prev => (prev?.ma_image_id === updatedImage.ma_image_id ? updatedImage : prev));
    };

    return (
        <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden">
            <div className="shrink-0 px-6 py-4 pb-1">
                <h1 className="mb-4 text-2xl font-bold">Image Library</h1>
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col px-6">
                <ImageLibraryBrowseShell
                    listing={listing}
                    imageGridProps={{
                        onEdit: image => setImageToEdit(image),
                        onEditAltText: image => setImageToEditAltText(image),
                        onPreview: image => setImagePreview(image),
                    }}
                    uploadImageCallback={() => {
                        setShowUploadModal(!showUploadModal);
                    }}
                />
            </div>

            <ImagePreviewModal
                onClose={() => setImagePreview(null)}
                image={imagePreview}
                onEditAltText={image => {
                    setImagePreview(null);
                    setImageToEditAltText(image);
                }}
            />

            <ImageEditAltTextModal
                key={imageToEditAltText?.ma_image_id ?? 'closed'}
                open={imageToEditAltText !== null}
                image={imageToEditAltText}
                onClose={() => setImageToEditAltText(null)}
                onSaveSuccess={handleImageAltTextUpdate}
            />

            <UploadImageModal
                open={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onUploadSuccess={(newImage: UploadedImageResponse) => {
                    setOnlyUnused(true);
                    setOnlyMyUploads(true);
                    if (allImages.find(img => img.ma_image_id === newImage.ma_image.ma_image_id)) {
                        const imagesExcludingNew = allImages.filter(img => img.ma_image_id !== newImage.ma_image.ma_image_id);
                        setAllImages([newImage.ma_image, ...imagesExcludingNew]);
                        return;
                    }
                    setAllImages([newImage.ma_image, ...allImages]);
                    setTotalCount(count => count + 1);
                }}
                onUploadFailure={(error?: Error) => {
                    const errorMessage = error ? error.message : 'There was an error uploading your image. Please try again.';
                    toast.error('Image Upload Failed', { description: errorMessage });
                }}
            />

            <ImageEditNameModal
                key={imageToEdit?.ma_image_id ?? 'closed'}
                open={imageToEdit !== null}
                onClose={() => setImageToEdit(null)}
                image={imageToEdit}
                imageName={imageToEdit?.filename ?? ''}
                onEditSuccess={(oldImage: MaImage, newImage: MaImage) => {
                    handleImageRename(oldImage, newImage);
                    setImageToEdit(null);
                }}
                onEditFailure={() => {
                    toast.error('Image Rename Failed', {
                        description: 'There was an error renaming your image. Please try again.',
                    });
                }}
            />
        </div>
    );
}
